import { customShader, glFilters, saturate } from "webgl-filters";
import { readSpecsImageBlobAsDataUrl } from "../specsImageUpload";
import { resolveSpecsImageUrl } from "../specsAssetUrls";

export type ImportUploadImageRole = "highlight" | "mask";

type GlImageData = {
  data: Uint8ClampedArray | ArrayLike<number>;
  width: number;
  height: number;
};

function glImageDataToPngDataUrl(result: GlImageData) {
  const canvas = document.createElement("canvas");
  canvas.width = result.width;
  canvas.height = result.height;

  const context = canvas.getContext("2d");
  if (!context) {
    throw new Error("Failed to process import image");
  }

  const imageData = context.createImageData(result.width, result.height);
  imageData.data.set(new Uint8ClampedArray(result.data));
  context.putImageData(imageData, 0, 0);

  return canvas.toDataURL("image/png");
}

async function resolveImportImageProcessingInputUrl(imageUrl: string) {
  const resolvedUrl = resolveSpecsImageUrl(imageUrl);

  if (resolvedUrl.startsWith("data:")) {
    return resolvedUrl;
  }

  const response = await fetch(resolvedUrl);
  if (!response.ok) {
    throw new Error(
      `Failed to read import image "${resolvedUrl}": ${response.status} ${response.statusText}`
    );
  }

  return readSpecsImageBlobAsDataUrl(await response.blob(), resolvedUrl);
}

export async function processImportUploadImageUrl(
  imageUrl: string,
  role: ImportUploadImageRole
) {
  const sourceUrl = await resolveImportImageProcessingInputUrl(imageUrl);
  const chain = glFilters();

  if (role === "highlight") {
    chain.addFilter(saturate({ factor: 0 }));
  } else {
    chain.addFilter(
      customShader({
        source: `
          vec4 color = texture2D(u_texture, v_texCoord);
          float mask = color.a;
          gl_FragColor = vec4(mask, mask, mask, 1.0);
        `,
      })
    );
  }

  const result = await chain.applyAsync(sourceUrl);
  return glImageDataToPngDataUrl(result);
}
